import {
  cloneArb,
  extractPlaceholders,
  getTranslation,
  getTranslationKeys,
  serializeArb,
  setTranslation,
  validateArbSet,
} from "./arb";
import { applyMetadataToArb, getEntryMetadata, getStatus, normalizeTags } from "./metadata";
import {
  LOCALES,
  type EntryMetadata,
  type Locale,
  type MetadataFile,
  type OpenedWorkspace,
  type TranslationStatus,
  type ValidationIssue,
} from "./types";

export interface WorkspaceCounts {
  keys: number;
  empty: number;
  overLimit: number;
  placeholderKeys: number;
  orphanMetadata: number;
  statuses: Record<Locale, Record<TranslationStatus, number>>;
}

export interface DirtyFile {
  filename: string;
  content: string;
  originalContent: string;
}

export interface ExportFile {
  filename: string;
  content: string;
}

function characterCount(value: string): number {
  return [...value].length;
}

export class EditorStore {
  readonly workspace: OpenedWorkspace;
  readonly keys: string[];
  selectedKey: string;
  readonly dirtyLocales = new Set<Locale>();
  metadataDirty = false;

  constructor(workspace: OpenedWorkspace) {
    this.workspace = workspace;
    this.keys = getTranslationKeys(workspace.arbs.en);
    this.selectedKey = this.keys[0] ?? "";
    for (const locale of workspace.normalizedLocales ?? []) this.dirtyLocales.add(locale);
    this.metadataDirty = workspace.metadataNormalized ?? false;
  }

  get isDirty(): boolean {
    return this.dirtyLocales.size > 0 || this.metadataDirty;
  }

  getTranslation(key: string, locale: Locale): string {
    return getTranslation(this.workspace.arbs[locale], key);
  }

  updateTranslation(key: string, locale: Locale, value: string): void {
    if (this.getTranslation(key, locale) === value) return;
    setTranslation(this.workspace.arbs[locale], key, value);
    this.dirtyLocales.add(locale);
  }

  getMetadata(key: string): EntryMetadata {
    return getEntryMetadata(this.workspace.metadata, key);
  }

  getStatus(key: string, locale: Locale): TranslationStatus {
    return getStatus(this.workspace.metadata, key, locale);
  }

  updateSharedMetadata(
    key: string,
    update: { description: string; note: string; tags: string[]; characterLimit: number | null },
  ): void {
    const existing = this.workspace.metadata.entries[key] ?? {};
    const next: EntryMetadata = { ...existing };
    const description = update.description.trim();
    const note = update.note.trim();
    const tags = normalizeTags(update.tags);

    if (description) next.description = description;
    else delete next.description;
    if (note) next.note = note;
    else delete next.note;
    if (tags.length) next.tags = tags;
    else delete next.tags;
    if (update.characterLimit !== null) next.characterLimit = update.characterLimit;
    else delete next.characterLimit;

    this.assignMetadata(key, next);
  }

  updateStatus(key: string, locale: Locale, status: TranslationStatus): void {
    const existing = this.workspace.metadata.entries[key] ?? {};
    const next: EntryMetadata = { ...existing, status: { ...(existing.status ?? {}) } };
    if (status === "new") delete next.status?.[locale];
    else if (next.status) next.status[locale] = status;
    if (next.status && Object.keys(next.status).length === 0) delete next.status;
    this.assignMetadata(key, next);
  }

  private assignMetadata(key: string, entry: EntryMetadata): void {
    if (Object.keys(entry).length === 0) delete this.workspace.metadata.entries[key];
    else this.workspace.metadata.entries[key] = entry;
    this.metadataDirty = true;
  }

  getOrphanMetadataKeys(): string[] {
    const keySet = new Set(this.keys);
    return Object.keys(this.workspace.metadata.entries).filter((key) => !keySet.has(key)).sort();
  }

  removeOrphanMetadata(key: string): void {
    if (!this.getOrphanMetadataKeys().includes(key)) return;
    delete this.workspace.metadata.entries[key];
    this.metadataDirty = true;
  }

  placeholdersFor(key: string): Record<Locale, string[]> {
    return Object.fromEntries(
      LOCALES.map((locale) => [locale, extractPlaceholders(this.getTranslation(key, locale))]),
    ) as Record<Locale, string[]>;
  }

  issues(): ValidationIssue[] {
    const issues = validateArbSet(this.workspace.arbs);
    for (const key of this.keys) {
      const metadata = this.getMetadata(key);
      const limit = metadata.characterLimit ?? null;
      for (const locale of LOCALES) {
        const value = this.getTranslation(key, locale);
        if (!value.trim()) {
          issues.push({ level: "warning", code: "empty", key, locale, message: `${key} is empty in ${locale.toUpperCase()}.` });
        }
        if (limit !== null && characterCount(value) > limit) {
          issues.push({
            level: "warning",
            code: "over-limit",
            key,
            locale,
            message: `${key} exceeds its ${limit}-character limit in ${locale.toUpperCase()}.`,
          });
        }
      }
    }
    return issues;
  }

  counts(): WorkspaceCounts {
    const statuses = Object.fromEntries(
      LOCALES.map((locale) => [locale, { new: 0, draft: 0, review: 0, final: 0 }]),
    ) as WorkspaceCounts["statuses"];
    let empty = 0;
    let overLimit = 0;
    let placeholderKeys = 0;

    for (const key of this.keys) {
      const metadata = this.getMetadata(key);
      const limit = metadata.characterLimit ?? null;
      if (extractPlaceholders(this.getTranslation(key, "de")).length) placeholderKeys += 1;
      for (const locale of LOCALES) {
        statuses[locale][this.getStatus(key, locale)] += 1;
        const value = this.getTranslation(key, locale);
        if (!value.trim()) empty += 1;
        if (limit !== null && characterCount(value) > limit) overLimit += 1;
      }
    }

    return {
      keys: this.keys.length,
      empty,
      overLimit,
      placeholderKeys,
      orphanMetadata: this.getOrphanMetadataKeys().length,
      statuses,
    };
  }

  dirtyFiles(): DirtyFile[] {
    const files: DirtyFile[] = [];
    const localesToWrite = new Set(this.dirtyLocales);
    if (this.metadataDirty) {
      applyMetadataToArb(this.workspace.arbs.en, this.workspace.metadata);
      localesToWrite.add("en");
    }
    for (const locale of LOCALES) {
      if (!localesToWrite.has(locale)) continue;
      const document = this.workspace.arbs[locale];
      files.push({ filename: document.filename, content: serializeArb(document), originalContent: document.originalText });
    }
    return files;
  }

  exportFiles(): ExportFile[] {
    const documents = Object.fromEntries(
      LOCALES.map((locale) => [locale, cloneArb(this.workspace.arbs[locale])]),
    ) as OpenedWorkspace["arbs"];
    applyMetadataToArb(documents.en, this.workspace.metadata);
    return LOCALES.map((locale) => ({
      filename: documents[locale].filename,
      content: serializeArb(documents[locale]),
    }));
  }

  markSaved(files: DirtyFile[]): void {
    for (const file of files) {
      const locale = LOCALES.find((candidate) => this.workspace.arbs[candidate].filename === file.filename);
      if (locale) {
        this.workspace.arbs[locale].originalText = file.content;
        this.dirtyLocales.delete(locale);
        if (locale === "en") this.metadataDirty = false;
      }
    }
  }
}
